import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface YearPickerProps {
  label: string;
  value: Date;
  onChange: (date: Date | null) => void;
  minDate: Date;
  maxDate: Date;
  availableYears?: number[];
}

const YearPicker: React.FC<YearPickerProps> = ({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  availableYears,
}) => {
  // Calculate effective min/max dates from available years
  const effectiveMinDate = availableYears && availableYears.length > 0
    ? new Date(Math.min(...availableYears), 0, 1)
    : minDate;
  const effectiveMaxDate = availableYears && availableYears.length > 0
    ? new Date(Math.max(...availableYears), 11, 31)
    : maxDate;

  const shouldDisableYear = availableYears && availableYears.length > 0
    ? (year: Date) => !availableYears.includes(year.getFullYear())
    : undefined;

  const commonSlotProps = {
    textField: { size: 'medium' as const, sx: { width: 120 } },
    desktopPaper: {
      sx: {
        paddingTop: '8px',
        borderRadius: 2,
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        '& .MuiPickersYear-root': {
          gap: 1,
        },
        '& .MuiPickersYear-yearButton': {
          height: 36,
          borderRadius: 1,
          '&.Mui-selected': {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          },
          // Hide disabled years completely instead of just graying them out
          '&.Mui-disabled': {
            display: 'none',
          }
        }
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label={label}
        views={['year']}
        value={value}
        onChange={onChange}
        minDate={effectiveMinDate}
        maxDate={effectiveMaxDate}
        shouldDisableYear={shouldDisableYear}
        slotProps={commonSlotProps}
      />
    </LocalizationProvider>
  );
};

export default YearPicker;
